import { AIChatMessage } from '@microsoft/ai-chat-protocol';
import { parseMarkdown } from '../../../services/parseUtils';

export type ParsedMessage = {
  content: React.ReactNode;
  citations: string[];
  followupQuestions: string[];
  role: string;
  context?: object;
};

export function parseMessage(message: AIChatMessage): ParsedMessage {
  if (message.role === 'user') {
    return {
      content: message.content,
      citations: [],
      followupQuestions: [],
      role: message.role,
      context: message.context,
    };
  }

  const citations: string[] = [];
  const followupQuestions: string[] = [];

  // Extract any follow-up questions that might be in the message
  if (!message.content) {
    message.content = '';
  }
  let text = message.content
    .replaceAll(/<<([^>]+)>>/g, (_match, content: string) => {
      followupQuestions.push(content);
      return '';
    })
    .split('<<')[0] // Truncate incomplete questions
    .trim();

  // Extract any citations that might be in the message, but ignore markdown links
  // Allow URLs with optional " page: N" after them as valid citations
  const urlRegex = /^(https?:\/\/[^\s\]]+)(?: page: \d+)?$/i;
  const parts = text.split(/(\[[^\]]+])(?!\()/g);
  const result = parts.map((part, index) => {
    // Only process citation-like parts (odd indices)
    if (index % 2 === 1) {
      // Remove the brackets
      const citation = part.slice(1, -1);
      // Only treat as citation if it is a valid URL
      if (urlRegex.test(citation)) {
        let citationIndex = citations.indexOf(citation);
        if (citationIndex === -1) {
          citations.push(citation);
          citationIndex = citations.length;
        } else {
          citationIndex++;
        }
        return `<sup>[${citationIndex}]</sup>`;
      }
      // If not a valid URL, return as-is (with brackets)
      return `[${citation}]`;
    }
    return part;
  });

  // Filter out null values and join the parts
  result.filter((part) => part !== null);
  const resultText = result.length > 0 ? result.join(' ') : '';

  return {
    content: resultText.length > 0 ? parseMarkdown(resultText) : null,
    citations,
    followupQuestions,
    role: message.role,
    context: message.context,
  };
}
